import { FC, Fragment, useState } from 'react';
import {
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
} from 'antd';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { OfficeObservationWarehouse } from '@/libs/fleet-api/services/types';
import {
	useSelectAddress,
	useSelectOfficeOwnershipStatus,
} from '@/libs/fleet-api/hooks/master-data';
import { AddressMasterData } from '@/libs/fleet-api/services/master-data';
import dayjs from 'dayjs';
import { OfficeOwnershipStatus } from '@/libs/fleet-api/enums';
import { inputCurrency } from '@/libs/antd/input-number';
import { useFBDHVisitReportApprovalPage } from './VisitReportApproval';

export const useWarehouseForm = () => {
	return useForm<OfficeObservationWarehouse<'payload'>>();
};

const FBDHVisitReportWarehouse: FC = () => {
	const { form, fieldName } = useWarehouseForm();
	const { officeObservation, surveyInformation } =
		useFBDHVisitReportApprovalPage();
	const { warehouse, technicalAspect } = officeObservation ?? {};

	const selectAddress = useSelectAddress();
	const [selectedAddress] = useState<AddressMasterData | null>(
		warehouse?.warehousePostalCode ?? null,
	);
	const selectOfficeOwnershipStatus = useSelectOfficeOwnershipStatus();

	const isSameAddress = Form.useWatch(
		['isWarehouseAddressSameAsCustomer'],
		form,
	);

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			name="warehouse"
			form={form}
			labelCol={{
				span: 10,
			}}
			initialValues={
				{
					surveyId: surveyInformation?.id,
					warehouseAddress: warehouse?.warehouseAddress,
					warehouseRt: warehouse?.warehouseRt,
					warehouseRw: warehouse?.warehouseRw,
					warehousePostalId: warehouse?.warehousePostalCode?.postalId,
					warehousePhoneNo: warehouse?.warehousePhoneNo,
					warehouseOwnershipStatusId: warehouse?.warehouseOwnershipStatus?.id,
					warehouseOccupationDate: warehouse?.warehouseOccupationDate,
					warehouseRentalStartDate: warehouse?.warehouseRentalStartDate,
					warehouseRentalEndDate: warehouse?.warehouseRentalEndDate,
					warehouseAnnualRentalFee: warehouse?.warehouseAnnualRentalFee,
					warehouseSize: warehouse?.warehouseSize,
					warehouseCapacity: warehouse?.warehouseCapacity,
					warehouseHeadcount: warehouse?.warehouseHeadcount,
					specializedExpertise: warehouse?.specializedExpertise,
					materialOwnershipTypeId: warehouse?.materialOwnershipType?.id,
					materialOwnershipPortion: warehouse?.materialOwnershipPortion,
					warehouseNotes: warehouse?.warehouseNotes,
					isWarehouseAddressSameAsCustomer:
						warehouse?.isWarehouseAddressSameAsCustomer,
				} as OfficeObservationWarehouse<'payload'>
			}
		>
			<Row
				gutter={[20, 40]}
				align="middle"
				style={{
					marginBottom: 24,
				}}
			>
				<Col span={24}>
					<Form.Item name={fieldName('isWarehouseAddressSameAsCustomer')}>
						<Checkbox
							disabled
							value={isSameAddress}
						>
							Checklist kotak disamping apabila{' '}
							<strong style={{ color: '#1557BC' }}>Alamat Usaha</strong> juga
							merupakan{' '}
							<strong style={{ color: '#1557BC' }}>
								Alamat Perusahaan sesuai NPWP
							</strong>
						</Checkbox>
					</Form.Item>
				</Col>
			</Row>
			<Row gutter={[20, 40]}>
				<Col span={24}>
					<Form.Item
						name={fieldName('warehouseAddress')}
						label="Alamat"
						labelCol={{ span: 5 }}
						style={{
							marginLeft: -5,
						}}
					>
						<Input disabled />
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[20, 40]}>
				<Col span={12}>
					<Form.Item
						label="RT/RW"
						required
					>
						<Row
							wrap={false}
							gutter={8}
						>
							<Col flex="auto">
								<Form.Item
									name={fieldName('warehouseRt')}
									noStyle
								>
									<Input disabled />
								</Form.Item>
							</Col>
							<Col flex="none">-</Col>
							<Col flex="auto">
								<Form.Item
									name={fieldName('warehouseRw')}
									noStyle
								>
									<Input disabled />
								</Form.Item>
							</Col>
						</Row>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('warehousePostalId')}
						label="Kode Pos"
					>
						<Select
							{...selectAddress}
							options={[
								{
									label: selectedAddress?.postalCode,
									value: selectedAddress?.postalId,
								},
							]}
							disabled={isSameAddress}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[20, 40]}>
				<Col span={12}>
					<Form.Item label="Kelurahan">
						<Input
							disabled
							value={selectedAddress?.locationName ?? '-'}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item label="Kecamatan">
						<Input
							disabled
							value={selectedAddress?.districtName ?? '-'}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[20, 40]}>
				<Col span={12}>
					<Form.Item label="Kota">
						<Input
							disabled
							value={selectedAddress?.cityName ?? '-'}
						/>
					</Form.Item>
				</Col>

				<Col span={12}>
					<Form.Item
						label="No. Telepon"
						name={fieldName(['warehousePhoneNo'])}
					>
						<Input disabled />
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row
				gutter={[20, 40]}
				align="top"
			>
				<Col span={12}>
					<Form.Item
						name={fieldName(['warehouseOwnershipStatusId'])}
						label="Status Kepemilikan Workshop/Gudang"
					>
						<Select
							{...selectOfficeOwnershipStatus}
							options={[
								{
									label: technicalAspect?.officeOwnershipStatus.value,
									value: technicalAspect?.officeOwnershipStatus.id,
								},
							]}
							disabled
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item shouldUpdate>
						{({ getFieldValue }) => {
							const status = getFieldValue(
								fieldName(['warehouseOwnershipStatusId']),
							) as OfficeOwnershipStatus | undefined;

							if (!status) return null;

							if (status === OfficeOwnershipStatus.Sewa) {
								return (
									<Fragment>
										<Form.Item
											label="Periode Sewa"
											labelCol={{
												span: 10,
												style: { marginBottom: 20 },
											}}
											required
										>
											<Row gutter={[20, 40]}>
												<Col span={12}>
													<Form.Item
														name={fieldName(['warehouseRentalStartDate'])}
														getValueProps={(value) => ({
															value: value && dayjs(value),
														})}
														normalize={(value) =>
															value && dayjs(value).utc(true).toISOString()
														}
														noStyle
													>
														<DatePicker
															suffixIcon={null}
															placeholder="YYYY-MM-DD"
															disabled
														/>
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item
														name={fieldName(['warehouseRentalEndDate'])}
														getValueProps={(value) => ({
															value: value && dayjs(value),
														})}
														normalize={(value) =>
															value && dayjs(value).utc(true).toISOString()
														}
														noStyle
													>
														<DatePicker
															suffixIcon={null}
															placeholder="YYYY-MM-DD"
															disabled
															disabledDate={(now) =>
																now <
																dayjs(
																	getFieldValue(
																		fieldName(['warehouseRentalStartDate']),
																	),
																)
															}
														/>
													</Form.Item>
												</Col>
											</Row>
										</Form.Item>
										<Form.Item
											name={fieldName(['warehouseAnnualRentalFee'])}
											label="Biaya Sewa per Tahun Sebesar"
											labelCol={{
												span: 10,
												style: { marginBottom: 20 },
											}}
										>
											<InputNumber
												{...inputCurrency}
												disabled
											/>
										</Form.Item>
									</Fragment>
								);
							}

							return (
								<Form.Item
									name={fieldName(['warehouseOccupationDate'])}
									label="Mulai Menempati Sejak"
									getValueProps={(value) => ({
										value: value && dayjs(value),
									})}
									normalize={(value) =>
										value && dayjs(value).utc(true).toISOString()
									}
									style={{ paddingInline: 10 }}
									labelCol={{ span: 10 }}
								>
									<DatePicker.YearPicker disabled />
								</Form.Item>
							);
						}}
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[20, 40]}>
				<Col span={12}>
					<Form.Item
						label="Luas Workshop/Gudang"
						name={fieldName(['warehouseSize'])}
					>
						<InputNumber
							suffix="m2"
							disabled
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="Kapasitas Workshop/Gudang"
						name={fieldName(['warehouseCapacity'])}
					>
						<InputNumber disabled />
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[20, 40]}>
				<Col span={12}>
					<Form.Item
						label="Jumlah Karyawan"
						name={fieldName(['warehouseHeadcount'])}
					>
						<InputNumber disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="Keahlian Khusus"
						name={fieldName(['specializedExpertise'])}
					>
						<Input disabled />
					</Form.Item>
				</Col>
			</Row>
			<Divider />
		</Form>
	);
};

export default FBDHVisitReportWarehouse;
